// ── Core types ──────────────────────────────────────────────────────────────
// BREAKING CHANGE (v2.0.0): Organization added as tenant boundary.
// Workspace is now a data scope within an organization, not the tenant itself.
// PlatformContext includes organization + workspace + available workspaces.
//
// Data types derived from Zod schemas — function-bearing types remain hand-written.

import type { z } from 'zod'
import type {
  UserSchema,
  OrganizationSchema,
  WorkspaceSchema,
  AuthStateSchema,
  AgentSubscriptionSchema,
  DisplayFormatSchema,
} from './schemas'

// ── Re-export schemas for runtime validation ─────────────────────────────────

export {
  UserSchema,
  OrganizationSchema,
  WorkspaceSchema,
  AuthStateSchema,
  AgentSubscriptionSchema,
  DisplayFormatSchema,
} from './schemas'

// ── Schema-derived data types (backward-compatible) ──────────────────────────

export type User = z.infer<typeof UserSchema>

export type Organization = z.infer<typeof OrganizationSchema>

export type Workspace = z.infer<typeof WorkspaceSchema>

export type AuthState = z.infer<typeof AuthStateSchema>

export type AgentSubscription = z.infer<typeof AgentSubscriptionSchema>

export type DisplayFormat = z.infer<typeof DisplayFormatSchema>

export type DisplayLocale = DisplayFormat['locale']

/** Served until the platform host resolves the org's format — same as an org with no rules. */
export const DEFAULT_DISPLAY_FORMAT: DisplayFormat = {
  currency: 'USD',
  decimals: 2,
  locale: 'en-US',
}

// ── Function-bearing types (can't be Zod schemas) ───────────────────────────

/**
 * The caller's resolved permissions, host-supplied (ASC-8277).
 * `agentName` scopes the check to that agent's Casbin domain (role-bearing
 * agents); omit it for org-level checks — same signature as accesszen-react's
 * `canAccessSnapshot`, which the platform host closes `can` over.
 */
export type PlatformPermissions = {
  can: (resource: string, action: string, agentName?: string) => boolean
}

/** Deny-all fallback served until the platform host supplies the resolved surface. */
export const DENY_ALL_PERMISSIONS: PlatformPermissions = {
  can: () => false,
}

export type PlatformContext = {
  user: User
  organization: Organization
  workspace: Workspace
  workspaces: Workspace[]
  // NO token — JWT lives in HTTP-only cookie, auto-sent by browser.
  // Use credentials: 'include' on fetch calls.
  onWorkspaceChange: (workspaceId: string) => void
  onNavigate: (path: string) => void
  /** Platform gateway base URL — injected by the platform so agents never need to
   *  guess or hardcode environment-specific origins. Use this for ZenbrokerProvider
   *  and any direct gateway calls. Populated from NEXT_PUBLIC_GATEWAY_URL. */
  gatewayUrl: string
  /**
   * The caller's resolved permissions, host-supplied. A UI HINT ONLY — every
   * backend route re-checks, exactly as accesszen-react's own hook docstring
   * states. Resolved values rather than a client: an agent must not be able
   * to trigger a permission fetch, for the same reason it holds no token.
   * Optional only so hand-built contexts (tests, legacy callers) keep
   * compiling — the platform store always supplies it (deny-all until the
   * accesszen snapshot lands). Treat absence as deny.
   */
  permissions?: PlatformPermissions
  /**
   * The org's currency / decimals / locale, host-supplied (ASC-10759). Resolved
   * values, not a fetch handle: agents format with it and never call the gateway
   * for it. Optional only so hand-built contexts (tests, legacy callers) keep
   * compiling — the platform store always supplies it (platform default until
   * the org's rules resolve). Treat absence as DEFAULT_DISPLAY_FORMAT.
   */
  displayFormat?: DisplayFormat
}

export type NavigationAPI = {
  navigate: (path: string) => void
  currentAgentId: string | null
}

export type PlatformState = {
  auth: AuthState
  organization: Organization
  workspace: Workspace
  workspaces: Workspace[]
  navigation: NavigationAPI
  subscriptions: Map<string, AgentSubscription>
  gatewayUrl: string
  /** Set by the platform host once the accesszen snapshot resolves — see
   *  platform/components/PermissionsStoreBridge.tsx. Absent = deny-all. */
  permissions?: PlatformPermissions
  /** Set by the platform host once the org's display format resolves — see
   *  platform/components/DisplayFormatStoreBridge.tsx. Absent = platform default. */
  displayFormat?: DisplayFormat
}

// ── PlatformStore (pure TypeScript, no React) ───────────────────────────────

export class PlatformStore {
  private state: PlatformState
  private listeners: Set<() => void> = new Set()

  constructor(initial: PlatformState) {
    this.state = initial
  }

  getState(): PlatformState {
    return this.state
  }

  getAuth(): AuthState {
    return this.state.auth
  }

  getOrganization(): Organization {
    return this.state.organization
  }

  getWorkspace(): Workspace {
    return this.state.workspace
  }

  getWorkspaces(): Workspace[] {
    return this.state.workspaces
  }

  getNavigation(): NavigationAPI {
    return this.state.navigation
  }

  getPermissions(): PlatformPermissions {
    return this.state.permissions ?? DENY_ALL_PERMISSIONS
  }

  getDisplayFormat(): DisplayFormat {
    return this.state.displayFormat ?? DEFAULT_DISPLAY_FORMAT
  }

  getSubscription(agentId: string): AgentSubscription | undefined {
    return this.state.subscriptions.get(agentId)
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  toPlatformContext(): PlatformContext {
    return {
      user: this.state.auth.user,
      organization: this.state.organization,
      workspace: this.state.workspace,
      workspaces: this.state.workspaces,
      onWorkspaceChange: (workspaceId: string) => {
        const ws = this.state.workspaces.find((w) => w.id === workspaceId)
        if (ws) {
          this._setState({ workspace: ws })
        }
      },
      onNavigate: this.state.navigation.navigate,
      gatewayUrl: this.state.gatewayUrl,
      permissions: this.state.permissions ?? DENY_ALL_PERMISSIONS,
      displayFormat: this.state.displayFormat ?? DEFAULT_DISPLAY_FORMAT,
    }
  }

  /** Platform-only — update store state and notify subscribers. */
  _setState(partial: Partial<PlatformState>): void {
    this.state = { ...this.state, ...partial }
    this.listeners.forEach((listener) => listener())
  }
}

// ── Singleton accessors ─────────────────────────────────────────────────────

let _store: PlatformStore | null = null

export function initPlatformStore(initial: PlatformState): PlatformStore {
  _store = new PlatformStore(initial)
  return _store
}

export function getPlatformStore(): PlatformStore {
  if (!_store) throw new Error('PlatformStore not initialized. Call initPlatformStore() first.')
  return _store
}

// ── MFE mount helpers ────────────────────────────────────────────────────────

/**
 * Duck-type guard for PlatformStore.
 * Use this instead of `instanceof PlatformStore` in agent mount.tsx files —
 * instanceof fails across Module Federation bundle boundaries because each
 * bundle has its own copy of the class.
 */
export function isPlatformStore(val: PlatformContext | PlatformStore): val is PlatformStore {
  return typeof (val as PlatformStore).toPlatformContext === 'function'
}

// ── Agent Route Manifest ────────────────────────────────────────────────────
// Declares an agent's content routes (analogous to settingsManifest for settings pages).
// Routes are relative to the agent's BrowserRouter basename (/agent/<id>).

type _RouteEntryBase = {
  label: string
  icon?: string
  parent?: string
  /** Screen type hint for skills */
  screenType?: 'landing' | 'list' | 'detail' | 'form' | 'split' | 'analytics' | 'settings'
  /** Sidebar group label for this route (e.g. 'Configure', 'Monitor'); omit to hide from grouped nav */
  navGroup?: string
  hidden?: boolean
  /**
   * Renders the nav entry visible but inert — greyed, not clickable, "Coming
   * soon" tooltip. For a screen that is BUILT but not yet wired to its backend,
   * so users can see it is coming instead of it vanishing. Prefer this over
   * `hidden` for that case; `hidden` removes the entry entirely.
   */
  disabled?: boolean
  /** If true, active check uses exact pathname match instead of startsWith */
  matchExact?: boolean
}

// kind defaults to 'nav' when omitted — existing agent manifests without kind are unaffected.
export type AgentNavRouteEntry = _RouteEntryBase & { kind?: 'nav'; path: string }
export type AgentActionRouteEntry = _RouteEntryBase & { kind: 'action'; action: string }
export type AgentRouteEntry = AgentNavRouteEntry | AgentActionRouteEntry

export type AgentRouteManifest = {
  /** Agent's BrowserRouter basename — must match /agent/<agent-id> */
  basename: string
  /** All content routes the agent renders */
  routes: AgentRouteEntry[]
  /** Workspace-scoped URL segments to normalize before the MFE remounts. */
  workspaceSwitchResets?: Array<{
    fromPrefix: string
    to: string
  }>
}

// ── Re-export event bus types for convenience ───────────────────────────────

export type { EventBus, PlatformEvent } from '@platform/event-bus'

// ── Re-export the sanctioned fetch clients (FR-025) ──────────────────────────
// safeFetch (JSON + Zod), safeStream (SSE / streaming), safeDownload
// (binary / blob), safeFetchNoContent (204 No Content mutations). The legacy
// `apiFetch` (http.ts) was retired in ASC-6338 — every call goes through one
// of these so non-2xx responses always surface the same typed `ApiError`.

export {
  safeFetch,
  safeFetchOrThrow,
  safeStream,
  safeDownload,
  safeFetchNoContent,
  FetchValidationError,
  ApiError,
  isApiError,
  isFetchValidationError,
  // The SSE counterpart of the above: a streamed failure never reaches
  // `buildApiError`, because the response already left as 200. Ref: ASC-8440.
  apiErrorFromStreamProblem,
} from './safe-fetch'
export type { SafeFetchResult, SafeFetchSuccess, SafeFetchError } from './safe-fetch'

// ── User id -> display name (ASC-10699) ──────────────────────────────────────
// Framework-free; wrap `userLookupQueryOptions` in `useQuery`. Never render a raw
// user id — `getResolvedUser` returns null when unresolved, show a fallback.

export {
  ResolvedUserSchema,
  ResolveUsersResponseSchema,
  fetchResolvedUsers,
  getResolvedUser,
  getUserLabel,
  normalizeUserIds,
  userLookupQueryOptions,
} from './user-lookup'
export type { ResolvedUser, UserLookup } from './user-lookup'

// ── Standard QueryClient defaults (ASC-6341) ─────────────────────────────────
// One factory every agent's (and platform's) QueryProvider.tsx calls instead
// of hand-rolling its own retry/staleTime options.

export { createQueryClientDefaults } from './query-client-defaults'
export type {
  StandardQueryClientDefaults,
  QueryClientDefaultsOverrides,
} from './query-client-defaults'

// ── Platform → agent location sync (ASC-6393) ────────────────────────────────
// Framework-agnostic half only — the react-router hook that consumes these
// lives in the agent, where react-router-dom is already a dependency.

export {
  deriveAgentPathname,
  onPlatformNavigate,
  onPlatformNavigationIntent,
  notifyPlatformNavigation,
  requestPlatformNavigation,
} from './platform-navigation'

// ── Silver / enriched column display formatting (ASC-7018) ───────────────────
// Shared by data-enrichment's sample tables and data-ingestion's stage table,
// which render the same silver columns and so must agree on how a header and a
// number look. Confidence DETECTION stays at the call site — see column-format.ts.

export { humanizeColumnName, formatNumericCell } from './column-format'

// ── Cross-agent route handoff (ASC-10255) ────────────────────────────────────
// The carrier for the orchestrator "detect → expand → route" flow: base64url
// `?q=` + a per-tab sessionStorage record. Orchestrator writes, target agent
// reads on mount. Ref: ASC-10254.

export {
  writeRouteHandoff,
  readRouteHandoff,
  hasUrlRouteHandoff,
  clearRouteHandoff,
  isRouteHandoffConsumed,
  markRouteHandoffConsumed,
} from './route-handoff'
export type { RouteHandoff, WriteRouteHandoffInput, WriteRouteHandoffResult } from './route-handoff'

// ── Cross-agent routing core (ASC-10513) ─────────────────────────────────────
// Intent-detection → routable-targets logic, extracted from the orchestrator MFE
// so every agent can consult the orchestrator and offer a hand-off. Pairs with
// the `?q=` carrier above.

export {
  deriveRouteDecision,
  annotateRouteAccessibility,
  AGENT_ROUTE_TARGETS,
  ROUTE_TARGET_IDS,
} from './agent-routing'
export type { RouteCandidate, RouteTarget, RouteDecision } from './agent-routing'

export { createOrchestratorDetector } from './cross-agent-detect'
export type { CrossAgentDetection, CrossAgentDetector } from './cross-agent-detect'

// ── Walkthrough tour target attribute (ASC-10568) ────────────────────────────
// Shared by platform chrome (AgentFeaturesMode's sidebar links) and every
// agent that stamps its own in-page tour targets — see tour-target.ts.

export { TOUR_TARGET_ATTR, tourTargetKey, tourTargetSelector, tourTargetProps } from './tour-target'

// ── Walkthrough tour target KEYS for agents with no dedicated -contracts lib
// (ASC-10568) — see each file's own comment for why it lives here instead.

export { CONTRACT_COMPLIANCE_TOUR_TARGETS } from './tour-targets/contract-compliance'
export type { ContractComplianceTourTargetKey } from './tour-targets/contract-compliance'
export { SUPPLIER_RISK_INTELLIGENCE_TOUR_TARGETS } from './tour-targets/supplier-risk-intelligence'
export type { SupplierRiskIntelligenceTourTargetKey } from './tour-targets/supplier-risk-intelligence'
export { WORKPLACE_VLM_TOUR_TARGETS } from './tour-targets/workplace-vlm'
export type { WorkplaceVlmTourTargetKey } from './tour-targets/workplace-vlm'
